import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

/*
 * ── Supabase マイグレーション済み ──────────────────────────────────────
 * ALTER TABLE profile_views ADD COLUMN IF NOT EXISTS prefecture TEXT;
 * ALTER TABLE link_plays    ADD COLUMN IF NOT EXISTS prefecture TEXT;
 * ALTER TABLE sns_clicks    ADD COLUMN IF NOT EXISTS prefecture TEXT;
 * ALTER TABLE resonate_clicks (id, artist_user_id, prefecture, created_at)
 * ALTER TABLE fragment_clicks (id, artist_user_id, prefecture, created_at)
 * ─────────────────────────────────────────────────────────────────────
 */

/** セッション内で都道府県をキャッシュするキー */
const PREFECTURE_CACHE_KEY = 'geo_prefecture';

/**
 * Nominatim Reverse Geocoding で都道府県名を取得する
 * 取得できない場合は null を返す（トラッキングはブロックしない）
 */
async function fetchPrefectureFromCoords(
  lat: number,
  lon: number
): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=ja`,
      { headers: { 'Accept-Language': 'ja' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    // address.state が都道府県に相当する
    return (data?.address?.state as string) ?? null;
  } catch {
    return null;
  }
}

/**
 * ブラウザの Geolocation API → Nominatim で都道府県を取得する。
 * セッション内でキャッシュし、2回目以降は即座に返す。
 */
async function getPrefecture(): Promise<string | null> {
  // キャッシュ確認
  const cached = sessionStorage.getItem(PREFECTURE_CACHE_KEY);
  if (cached !== null) {
    return cached === '' ? null : cached;
  }

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      sessionStorage.setItem(PREFECTURE_CACHE_KEY, '');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const pref = await fetchPrefectureFromCoords(
          pos.coords.latitude,
          pos.coords.longitude
        );
        sessionStorage.setItem(PREFECTURE_CACHE_KEY, pref ?? '');
        resolve(pref);
      },
      () => {
        // 拒否・タイムアウト時は null
        sessionStorage.setItem(PREFECTURE_CACHE_KEY, '');
        resolve(null);
      },
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}

/**
 * トラッキングhook
 * プロフィール閲覧・リンク再生・SNS遷移・還元・グッズをSupabaseに記録する
 * sessionStorageで同一セッション内の重複記録を防止
 */
export function useTracking() {
  /**
   * プロフィール閲覧を記録
   */
  const recordProfileView = useCallback(
    async (artistUserId: string, viewerId?: string) => {
      if (!artistUserId) return;
      if (viewerId && viewerId === artistUserId) return;

      const sessionKey = `pv_${artistUserId}`;
      if (sessionStorage.getItem(sessionKey)) return;

      // 位置情報取得（非同期・失敗してもトラッキングは続行）
      const prefecture = await getPrefecture();

      try {
        const { error } = await supabase.from('profile_views').insert({
          artist_user_id: artistUserId,
          viewer_id: viewerId ?? null,
          prefecture: prefecture ?? null,
        });

        if (error) {
          console.error('Supabase error (profile view):', error);
          return;
        }

        sessionStorage.setItem(sessionKey, '1');
      } catch (err) {
        console.error('Failed to record profile view:', err);
      }
    },
    []
  );

  /**
   * リンク再生を記録
   */
  const recordLinkPlay = useCallback(async (artistUserId: string) => {
    if (!artistUserId) return;

    const sessionKey = `lp_${artistUserId}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const prefecture = await getPrefecture();

    try {
      const { error } = await supabase.from('link_plays').insert({
        artist_user_id: artistUserId,
        prefecture: prefecture ?? null,
      });

      if (error) {
        console.error('Supabase error (link play):', error);
        return;
      }

      sessionStorage.setItem(sessionKey, '1');
    } catch (err) {
      console.error('Failed to record link play:', err);
    }
  }, []);

  /**
   * SNS遷移を記録
   */
  const recordSnsClick = useCallback(
    async (artistUserId: string, platform: string) => {
      if (!artistUserId || !platform) return;

      const sessionKey = `sc_${artistUserId}_${platform}`;
      if (sessionStorage.getItem(sessionKey)) return;

      const prefecture = await getPrefecture();

      try {
        const { error } = await supabase.from('sns_clicks').insert({
          artist_user_id: artistUserId,
          platform,
          prefecture: prefecture ?? null,
        });

        if (error) {
          console.error('Supabase error (SNS click):', error);
          return;
        }

        sessionStorage.setItem(sessionKey, '1');
      } catch (err) {
        console.error('Failed to record SNS click:', err);
      }
    },
    []
  );

  /**
   * 還元ボタンのクリックを記録
   */
  const recordResonateClick = useCallback(async (artistUserId: string) => {
    if (!artistUserId) return;

    const sessionKey = `rc_${artistUserId}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const prefecture = await getPrefecture();

    try {
      const { error } = await supabase.from('resonate_clicks').insert({
        artist_user_id: artistUserId,
        prefecture: prefecture ?? null,
      });

      if (error) {
        console.error('Supabase error (resonate click):', error);
        return;
      }

      sessionStorage.setItem(sessionKey, '1');
    } catch (err) {
      console.error('Failed to record resonate click:', err);
    }
  }, []);

  /**
   * グッズボタンのクリックを記録
   */
  const recordFragmentClick = useCallback(async (artistUserId: string) => {
    if (!artistUserId) return;

    const sessionKey = `fc_${artistUserId}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const prefecture = await getPrefecture();

    try {
      const { error } = await supabase.from('fragment_clicks').insert({
        artist_user_id: artistUserId,
        prefecture: prefecture ?? null,
      });

      if (error) {
        console.error('Supabase error (fragment click):', error);
        return;
      }

      sessionStorage.setItem(sessionKey, '1');
    } catch (err) {
      console.error('Failed to record fragment click:', err);
    }
  }, []);

  return {
    recordProfileView,
    recordLinkPlay,
    recordSnsClick,
    recordResonateClick,
    recordFragmentClick,
  };
}